import { projectSchema } from '@/models/projectSchema';
import {
  PROJECTS_FETCHING_ERROR_PREFIX,
  PROJECT_REMOVAL_ERROR_PREFIX,
} from '@/redux/projects/projects.constants';
import { axiosInstance, axiosInstanceNewAPI } from '@/services/api/utils/axiosInstance';
import { PageOf, Project } from '@/types/models';
import { ThunkConfig } from '@/types/store';
import { getError } from '@/utils/error-report/getError';
import { validateDataUsingZodSchema } from '@/utils/validateDataUsingZodSchema';
import { extractAxiosErrorMessage } from '@/utils/getErrorMessage/getErrorMessage.utils';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { z } from 'zod';
import { logEntrySchema } from '@/models/logEntrySchema';
import { FetchLogsParams } from '@/types/logEntry';
import { pageableSchema } from '@/models/pageableSchema';
import { apiPath } from '../apiPath';

const MIN_PAGES_FOR_ADDITIONAL_FETCH = 1;
const START_PAGE_INDEX_FOR_ADDITIONAL_FETCH = 1;
const PROJECTS_PER_PAGE = 100;
const PAGE_INCREMENT = 1;

const logsResponseSchema = pageableSchema(logEntrySchema);

export type LogsResponse = z.infer<typeof logsResponseSchema>;

export const fetchProjectLogs = createAsyncThunk<LogsResponse, FetchLogsParams, ThunkConfig>(
  'projects/fetchLogs',
  async (params, { rejectWithValue }) => {
    try {
      const response = await axiosInstanceNewAPI.get(apiPath.getProjectLogs(params));

      const isDataValid = validateDataUsingZodSchema(response.data, logsResponseSchema);
      if (!isDataValid) {
        throw new Error('Invalid logs data received from server');
      }

      return response.data;
    } catch (error) {
      let errorMessage = 'Error fetching project logs';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  },
);

export const getAllProjects = createAsyncThunk<Project[], void, ThunkConfig>(
  'project/getAllProjects',
  async () => {
    try {
      const firstPageResponse = await axiosInstanceNewAPI.get<PageOf<Project>>(
        apiPath.getProjects(),
        {
          params: { page: 0, size: PROJECTS_PER_PAGE },
        },
      );
      let allProjects: Project[] = [];
      if (firstPageResponse.data && Array.isArray(firstPageResponse.data.content)) {
        allProjects = [...firstPageResponse.data.content];
        if (
          typeof firstPageResponse.data.totalPages === 'number' &&
          firstPageResponse.data.totalPages > MIN_PAGES_FOR_ADDITIONAL_FETCH
        ) {
          const { totalPages } = firstPageResponse.data;

          const additionalPageRequests = [];
          for (
            let page = START_PAGE_INDEX_FOR_ADDITIONAL_FETCH;
            page < totalPages;
            page += PAGE_INCREMENT
          ) {
            additionalPageRequests.push(
              axiosInstanceNewAPI.get<PageOf<Project>>(apiPath.getProjects(), {
                params: { page, size: PROJECTS_PER_PAGE },
              }),
            );
          }

          const EMPTY_ARRAY_LENGTH = 0;
          if (additionalPageRequests.length > EMPTY_ARRAY_LENGTH) {
            const additionalPagesResponses = await Promise.all(additionalPageRequests);

            additionalPagesResponses.forEach(pageResponse => {
              if (pageResponse.data && Array.isArray(pageResponse.data.content)) {
                allProjects = [...allProjects, ...pageResponse.data.content];
              }
            });
          }
        }
      } else {
        throw new Error('Unexpected response structure received from server');
      }

      return allProjects;
    } catch (error) {
      return Promise.reject(getError({ error, prefix: PROJECTS_FETCHING_ERROR_PREFIX }));
    }
  },
);

export interface FileUploadResponse {
  id: number;
  filename: string;
  length: number;
  owner: string;
  uploadedDataLength: number;
}

export interface CreateProjectPayload {
  name: string;
  license?: string;
  customLicenseName?: string;
  customLicenseUrl?: string;
  projectId?: string;
  disease?: string;
  organism?: string;
  version?: string;
  notifyEmail?: string;
  sbgnFormat?: boolean;
  fileId: string;
}

export const uploadFile = createAsyncThunk<FileUploadResponse, File, ThunkConfig>(
  'projects/uploadFile',
  async (file, { rejectWithValue }) => {
    try {
      // Use URLSearchParams for application/x-www-form-urlencoded content type
      const params = new URLSearchParams();
      params.append('filename', file.name);
      params.append('length', file.size.toString());

      const createFileResponse = await axiosInstance.post('files/', params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      const fileData = createFileResponse.data;

      const fileUploadResponseSchema = z.object({
        id: z.number(),
        filename: z.string(),
        length: z.number(),
        owner: z.string(),
        uploadedDataLength: z.number(),
      });

      const isDataValid = validateDataUsingZodSchema(fileData, fileUploadResponseSchema);
      if (!isDataValid) {
        throw new Error('Invalid file upload response from server');
      }

      const fileContent = await file.arrayBuffer();

      await axiosInstance.post(`files/${fileData.id}:uploadContent`, fileContent, {
        headers: {
          'Content-Type': 'application/octet-stream',
        },
      });

      return fileData as FileUploadResponse;
    } catch (error) {
      let errorMessage = 'Error uploading file';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  },
);

export interface CreateProjectWithFilePayload {
  file: File;
  name: string;
  license?: string;
  customLicenseName?: string;
  customLicenseUrl?: string;
  projectId?: string;
  disease?: string;
  organism?: string;
  version?: string;
  notifyEmail?: string;
  sbgnFormat?: boolean;
}

export const createProjectWithFile = createAsyncThunk<
  Project,
  CreateProjectWithFilePayload,
  ThunkConfig
>('projects/createProjectWithFile', async (data, { rejectWithValue }) => {
  try {
    const formData = new FormData();

    formData.append('file', data.file);

    formData.append('name', data.name);
    if (data.license && data.license !== 'OTHER') formData.append('license', data.license);
    if (data.license === 'OTHER' && data.customLicenseName) {
      formData.append('customLicenseName', data.customLicenseName);
    } else if (data.license === 'OTHER') {
      formData.append('customLicenseName', '');
    }
    if (data.license === 'OTHER' && data.customLicenseUrl) {
      formData.append('customLicenseUrl', data.customLicenseUrl);
    } else if (data.license === 'OTHER') {
      formData.append('customLicenseUrl', '');
    }
    if (data.projectId) formData.append('projectId', data.projectId);
    if (data.disease) formData.append('disease', data.disease);
    if (data.organism) formData.append('organism', data.organism);
    if (data.version) formData.append('version', data.version);
    if (data.notifyEmail) formData.append('notifyEmail', data.notifyEmail);
    if (data.sbgnFormat !== undefined) formData.append('sbgnFormat', String(data.sbgnFormat));

    const response = await axiosInstanceNewAPI.post<Project>(apiPath.getProjects(), formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    const isDataValid = validateDataUsingZodSchema(response.data, projectSchema);

    if (!isDataValid) {
      throw new Error('Invalid project data received from server');
    }

    return response.data;
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Error creating project with file';

    return rejectWithValue(errorMsg);
  }
});

export const createProject = createAsyncThunk<Project, CreateProjectPayload, ThunkConfig>(
  'projects/createProject',
  async (projectData, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();

      const dataToSend = { ...projectData };

      if (dataToSend.license === 'OTHER') {
        if (!dataToSend.customLicenseName) dataToSend.customLicenseName = '';
        if (!dataToSend.customLicenseUrl) dataToSend.customLicenseUrl = '';
      }

      Object.entries(dataToSend).forEach(([key, value]) => {
        if (value !== undefined) {
          params.append(key, String(value));
        }
      });

      const response = await axiosInstance.post<Project>('projects/', params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      const isDataValid = validateDataUsingZodSchema(response.data, projectSchema);

      if (!isDataValid) {
        throw new Error('Invalid project data received from server');
      }

      return response.data;
    } catch (error) {
      let errorMessage = 'Error creating project';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  },
);

export const removeProject = createAsyncThunk<string, string, ThunkConfig>(
  'projects/removeProject',
  async (projectId, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(apiPath.removeProject(projectId));
      return projectId;
    } catch (error: unknown) {
      let errorMessage = PROJECT_REMOVAL_ERROR_PREFIX;

      if (error instanceof Error) {
        if (axios.isAxiosError(error)) {
          errorMessage += extractAxiosErrorMessage(error);
        } else {
          errorMessage += error.message;
        }
      } else if (typeof error === 'string') {
        errorMessage += error;
      }

      return rejectWithValue(errorMessage);
    }
  },
);

export const shareProjectInMinervaNet = createAsyncThunk<string, string, ThunkConfig>(
  'projects/shareProjectInMinervaNet',
  async (projectId, { rejectWithValue }) => {
    try {
      await axiosInstance.post(
        apiPath.registerProject(projectId),
        {},
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
      );
      return projectId;
    } catch (error) {
      return rejectWithValue('Failed to share project in Minerva-Net');
    }
  },
);

export const unshareProjectInMinervaNet = createAsyncThunk<string, string, ThunkConfig>(
  'projects/unshareProjectInMinervaNet',
  async (projectId, { rejectWithValue }) => {
    try {
      await axiosInstance.post(
        apiPath.unregisterProject(projectId),
        {},
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
      );
      return projectId;
    } catch (error) {
      return rejectWithValue('Failed to unshare project from Minerva-Net');
    }
  },
);

export const archiveProject = createAsyncThunk<string, string, ThunkConfig>(
  'projects/archiveProject',
  async (projectId, { rejectWithValue }) => {
    try {
      await axiosInstance.post(
        apiPath.archiveProject(projectId),
        {},
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
      );

      return projectId;
    } catch (error) {
      let errorMessage = 'Failed to archive project';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  },
);

export const reviveProject = createAsyncThunk<string, string, ThunkConfig>(
  'projects/reviveProject',
  async (projectId, { rejectWithValue }) => {
    try {
      await axiosInstance.post(
        apiPath.reviveProject(projectId),
        {},
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
      );

      return projectId;
    } catch (error) {
      let errorMessage = 'Failed to unarchive project';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  },
);

export interface GetZipFromGitPayload {
  url: string;
  branch?: string;
}

export const getZipFileFromGit = createAsyncThunk<File, GetZipFromGitPayload, ThunkConfig>(
  'projects/getZipFromGit',
  async (gitData, { rejectWithValue }) => {
    try {
      const dataToSend = { ...gitData };

      const response = await axiosInstanceNewAPI.post<Blob>(apiPath.getZipFromGit(), dataToSend, {
        headers: {
          'Content-Type': 'application/json',
        },
        responseType: 'blob',
      });

      const filename = `git-export-${Date.now()}.zip`;
      const file = new File([response.data], filename, { type: 'application/zip' });

      return file;
    } catch (error) {
      let errorMessage = 'Error downloading zip from git';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  },
);
